<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "Devices",
};
</script>

<script setup lang="ts">
import DealerService from "@/api/dealer";
import ServiceService from "@/api/service";
import ChildService from "@/api/child";
import DevicesTable from "@/components/models/device/DeviceTable.vue";
import { useSnackStore } from "@/stores/snack";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";

import DeviceService, { type TDevice } from "@/api/device";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import { generateFormOptions, type PaginatedType, type VFormFieldChoice, type VFormOptions } from "@/utils/DjangoTypes";
import { useAbility } from "@casl/vue";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";

definePage({
  meta: {
    title: "Devices",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.DEVICE);
    },
  },
});

const snackStore = useSnackStore();
const devicesFetchEnabled = ref(false);

const checkOptions = ref<VFormOptions>({});
const serviceChoices = ref<VFormFieldChoice<any>[]>([]);
const dealerChoices = ref<VFormFieldChoice<any>[]>([]);
const childChoices = ref<VFormFieldChoice<any>[]>([]);

const queriesTableParams: Record<string, ServerTableParamsType> = {
  devices: DefaultServerTableParams,
};
provide("queriesTableParams", queriesTableParams);

const {
  data: devices,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["devices"],
  queryFn: async (): Promise<PaginatedType<TDevice[]> | undefined> => {
    try {
      const [devices, deviceCheckOptions, servicesSelectable, dealersSelectable, childrenSelectable] = await Promise.all([
        DeviceService.device.getAll(queriesTableParams.devices),
        DeviceService.deviceCheck.getOptions(),
        ServiceService.service.getAllSelectable({ ...DefaultServerTableParams, itemsPerPage: 100 }),
        DealerService.dealer.getAllSelectable(),
        ChildService.child.getAllSelectable(),
      ]);

      checkOptions.value = generateFormOptions(deviceCheckOptions.data.actions.POST);
      serviceChoices.value = servicesSelectable;
      dealerChoices.value = dealersSelectable;
      childChoices.value = childrenSelectable;

      return devices.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => devicesFetchEnabled.value,
});

onMounted(async () => {
  devicesFetchEnabled.value = true;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <devices-table
        :query-key="'devices'"
        :data="devices"
        :check-options="checkOptions"
        :services="serviceChoices"
        :dealers="dealerChoices"
        :children="childChoices"
        :loading="isPending || isFetching"
      />
    </div>
  </v-container>
</template>

<style scoped></style>
